import {
  CrystalContainerAssignments,
  CrystalDataDTO,
  DewarDataDTO,
  PuckDataDTO,
} from '../constants';

export const getOccupiedPinsOfCurrentPuck = (
  puckId: number,
  crystals: CrystalDataDTO[],
): { crystalId: number; pinPosition: number; isDirty: boolean }[] =>
  crystals
    ?.filter((item) => item?.crystalContainerAssignments?.puckId === puckId)
    .map((item) => ({
      crystalId: item?.id,
      pinPosition: item?.crystalContainerAssignments?.puckPinPosition,
      isDirty: item?.crystalContainerAssignments?.isDirty,
    }));

export const getFirstAvailablePinOfPuck = (occupiedPins, totalPins) => {
  const availablePin = occupiedPins.reduce((acc, cur) => {
    return cur.pinPosition === acc ? acc + 1 : acc;
  }, 1);
  if (availablePin > totalPins) {
    return null;
  }
  return availablePin;
};

export const getUpdatedCrystalsWithDewarByPuckId = (
  crystals: CrystalDataDTO[],
  puckId: number,
  dewarId: number,
) => {
  return crystals.map((crystal: CrystalDataDTO) => {
    if (crystal.crystalContainerAssignments.puckId === puckId) {
      return {
        ...crystal,
        crystalContainerAssignments: {
          ...crystal.crystalContainerAssignments,
          dewarId: dewarId,
          isDirty: true,
        },
      };
    } else {
      return crystal;
    }
  });
};

export const getUpdatedCrystalsAfterCrystalContainerAssignments = (
  crystals: CrystalDataDTO[],
  selectedCrystalId: number,
  assignments: CrystalContainerAssignments,
) => {
  let crystalsCopy = [...crystals];
  const selectedCrystalIndex = crystals.findIndex(
    (crystal) => crystal.id === selectedCrystalId,
  );
  const selectedCrystal = crystals[selectedCrystalIndex];
  if (assignments.puckId) {
    assignments.dewarId =
      getDewarIdForPuck(crystals, assignments.puckId) ?? assignments.dewarId;
  }

  const updatedCrystal: CrystalDataDTO = {
    ...selectedCrystal,
    crystalContainerAssignments: {
      ...selectedCrystal?.crystalContainerAssignments,
      ...assignments,
    },
  };
  crystalsCopy[selectedCrystalIndex] = updatedCrystal;
  if (assignments.dewarId && selectedCrystal?.crystalContainerAssignments.puckId) {
    crystalsCopy = getUpdatedCrystalsWithDewarByPuckId(
      crystalsCopy,
      selectedCrystal.crystalContainerAssignments.puckId,
      assignments.dewarId,
    );
  }
  return crystalsCopy;
};

export const getPuckById = (pucks: PuckDataDTO[], puckId: number): PuckDataDTO => {
  return pucks?.find((puck) => puck.id === puckId);
};

export const getDewarById = (dewars: DewarDataDTO[], dewarId: number): DewarDataDTO => {
  return dewars?.find((dewar) => dewar.id === dewarId);
};

export const getCrystalsByPuckId = (crystals: CrystalDataDTO[], puckId: number) => {
  return crystals.filter(
    (crystal) => puckId && crystal?.crystalContainerAssignments.puckId === puckId,
  );
};

export const getCrystalsByDewarId = (crystals: CrystalDataDTO[], dewarId: number) => {
  return crystals.filter(
    (crystal) => dewarId && crystal?.crystalContainerAssignments.dewarId === dewarId,
  );
};

export const getCrystalById = (
  crystals: CrystalDataDTO[],
  crystalId: number,
): CrystalDataDTO => {
  return crystals?.find((crystal) => crystal.id === crystalId);
};

export const getDewarIdForPuck = (
  crystals: CrystalDataDTO[],
  puckId: number,
): number | null =>
  crystals
    .filter((c) => c.crystalContainerAssignments.puckId === puckId)
    .find((c) => c.crystalContainerAssignments.dewarId)?.crystalContainerAssignments
    ?.dewarId;

export const getPuckIdsOfDewar = (
  crystals: CrystalDataDTO[],
  dewarId: number,
): number[] => [
  ...new Set(
    getCrystalsByDewarId(crystals, dewarId).map(
      (crystal) => crystal.crystalContainerAssignments.puckId,
    ),
  ),
];

const isPuckFull = (puck: PuckDataDTO, crystals: CrystalDataDTO[]): boolean =>
  !puck || puck.pinCount - getCrystalsByPuckId(crystals, puck.id).length <= 0;

export const getDewarSlotAvailable = (
  dewar: DewarDataDTO,
  pucksOfSelectedDewar: number[],
  pucks: PuckDataDTO[],
  crystals: CrystalDataDTO[],
  selectedPuckId: number | undefined,
): number => {
  const puckCountSlotAvailable = dewar.puckCount - pucksOfSelectedDewar.length;
  if (puckCountSlotAvailable > 0) {
    return puckCountSlotAvailable;
  }

  const isSelectedPuckInDewar =
    !!selectedPuckId && pucksOfSelectedDewar.includes(selectedPuckId);
  const hasAvailablePinInSelectedPuck =
    isSelectedPuckInDewar && !isPuckFull(getPuckById(pucks, selectedPuckId), crystals);
  return hasAvailablePinInSelectedPuck ? 1 : 0;
};
